import React, { useEffect, useRef, useState } from 'react';
import AccessModuleService from '../../services/AccessModuleService';
import { useUserStore } from '../../contexts/userStore';
import { useSSEContext } from '../../contexts/sse';
import { AccessIcons } from '../../utils/accessIcons';
import { ModuleIps, ModuleType, ModuleTypeText } from '../../enums/moduleType';
import './AccessMonitoring.css';
import { useNavigate } from 'react-router-dom';
import { wait } from '../../utils/timer';
import { LuDoorClosed, LuDoorOpen, LuLock, LuLockOpen, LuWifi, LuWifiOff } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';

const AccessMonitoring = () => {
   const { selectedCondominio } = useUserStore();
   const { sseReceived } = useSSEContext();
   const [moduleStates, setModuleStates] = useState([]);
   const [moduleConnections, setModuleConnections] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const navigate = useNavigate();
   const cancelTokenRef = useRef(null);
   const { t } = useTranslation();

   async function fetchModuleState() {
      try {
         if (cancelTokenRef.current) {
            cancelTokenRef.current.abort();
         }
         cancelTokenRef.current = new AbortController();
         const { data } = await AccessModuleService.listLastStatesByCondominium(selectedCondominio, cancelTokenRef.current.signal);
         const states = [];
         if (data && Array.isArray(data)) {
            for (const moduleState of data) {
               const garage = moduleState.garage || moduleState.garageIp;
               if (garage) {
                  for (let i = 1; i <= 4; i++) {
                     if (garage[`enabled${i}`]) {
                        states.push({
                           ...moduleState,
                           index: i,
                           name: garage[`name${i}`],
                        });
                     }
                  }
               } else {
                  states.push(moduleState);
               }
            }
         }
         setModuleStates(states);
      } catch (error) {
         console.error(error);
      } finally {
         setIsLoading(false);
      }
   }

   useEffect(() => {
      fetchModuleState();
   }, [selectedCondominio]);

   useEffect(() => {
      if (sseReceived) {
         if (['moduleState', 'moduleConnection'].includes(sseReceived.action)) {
            fetchModuleState();
         }
      }
   }, [sseReceived]);

   return (
      <div className=''>
         {isLoading ? (
            <div className='grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 flex-wrap gap-4!'>
               {Array(12).fill(0).map((_, index) => (
                  <div style={{ '--shimmerDelay': `${Math.random() * 100}ms` }} key={index} className='group flex flex-col w-full h-[190px] rounded-lg shimmer'></div>
               ))}
            </div>
         ) : moduleStates.length > 0 ? (
            <div className='grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 flex-wrap gap-4!'>
               {moduleStates.map(_module => (
                  <div key={_module.uuid + _module.index} className='group flex flex-col w-full h-[190px]'>
                     <div className='group-hover:rotate-y-180 duration-500 transform-3d perspective-distant size-full text-(--textColor) items-center justify-center text-center p-4! box-border! rounded-lg border! border-(--borderColor)! shadow-(--box-shadow)'>
                        <div className='absolute inset-0 size-full flex flex-col transform-3d backface-hidden items-center justify-center'>
                           <h3 className='text-lg! font-semibold! mb-3!'>{_module.name}</h3>
                           <span className='inline-flex px-4! py-2! mb-4! rounded-full opacity-60 text-[0.84rem]! border-(--borderColor)! border! gap-2'>
                              {/* {AccessIcons[_module.type]} */}
                              {t(ModuleTypeText[_module.type])}
                           </span>
                           <div className='flex justify-center items-center gap-4'>
                              {ModuleIps.includes(_module.type) && (
                                 <span className={`module-connection ${_module.state.connection}`}>
                                    {_module.state.connection === 'connected' ? (
                                       <div className="inline-flex items-center flex-row gap-1.5 leading-0">
                                          <LuWifi size={22} />
                                          Online
                                       </div>
                                    ) : (
                                       <div className="inline-flex items-center flex-row gap-1.5 leading-0">
                                          <LuWifiOff size={22} />
                                          Offline
                                       </div>
                                    )}
                                 </span>
                              )}
                              {![ModuleType.TurnstileModule, ModuleType.IpTurnstileModule, ModuleType.KioskIpModule].includes(_module.type) && (
                                 <span className={`module-state ${_module.state.state}`}>
                                    {_module.state.state === 'open' ? (
                                       <div className="inline-flex items-center flex-row gap-1.5 leading-0">
                                          <LuLockOpen size={20} />
                                          {t("common.open")}
                                       </div>
                                    ) : (
                                       <div className="inline-flex items-center flex-row gap-1.5 leading-0">
                                          <LuLock size={20} />
                                          {t("common.closed")}
                                       </div>
                                    )}
                                 </span>
                              )}
                           </div>
                        </div>
                        <button
                           type='button'
                           className='cursor-pointer absolute inset-0 size-full flex flex-col items-center justify-center rotate-y-180 transform-3d backface-hidden'
                           onClick={e => navigate(`/relatorios/historico-modulos-de-acessos?m=${_module.uuid}${_module.index ? `:${_module.index - 1}` : ''}`)}
                        >
                           <span className='text-[2rem]! block mb-1.5!'>{AccessIcons[_module.type]}</span>
                           <h3 className='text-lg! mb-2!'>{_module.name}</h3>
                           <p>{t("common.viewHistory")}</p>
                        </button>
                     </div>
                  </div>
               ))}
            </div>
         ) : (
            <p>Nenhum módulo configurado</p>
         )}
      </div>
   );
};

export default AccessMonitoring;
